import React from 'react';

import PropTypes from 'prop-types';

import { DividingLine } from 'components/DividingLine/DividingLine';
import { AddressInput } from 'components/FormHelper/AddressInput/AddressInput';
import {
  COMPANY_FIELDS,
  FIELDS_TO_CLEAR_AFTER_COUNTRY,
  GERMANY_COUNTRY_CODE,
} from 'components/FormHelper/AddressInput/addressInput.constants';
import { FormField } from 'components/FormHelper/FormField';
import { InputRow } from 'components/FormHelper/InputRow';
import { MessageFieldType } from 'components/MyTaskArea/types';

import { COUNTRY_OPTIONS, KEYS_TO_GERMAN } from 'global/constants';

import { prepValueForDisplay } from 'utils/DataUtils';
import { parseEIVDateStringToEfixDateString } from 'utils/DatesUtils';
import {
  getCanonicalFieldName,
  getFieldLabel,
  getFieldType,
  getRadioOptions,
  isDateField,
} from 'utils/FieldNameMapping';

import {
  buildAddressValuesFromFields,
  createEntityScopedFormik,
  isAddressMessageField,
  mapCountryNameToCode,
  shouldShowFieldError,
} from './taskDetailAddressUtils';

const getCanonicalKey = (field) => getCanonicalFieldName(field.field);
const getFieldKey = (field) => `${field.entityId}_${getCanonicalKey(field)}`;

const resolveFieldLabel = (field, fieldKey) => {
  const candidates = [fieldKey, field.field].filter(Boolean);

  for (const candidate of candidates) {
    const mappingLabel = getFieldLabel(candidate);
    if (mappingLabel) {
      return mappingLabel;
    }

    if (KEYS_TO_GERMAN[candidate]) {
      return KEYS_TO_GERMAN[candidate];
    }
  }

  return field.field || fieldKey;
};

// The field name is a composite key (`entityId_field`), so FormField cannot
// resolve the type by name; it is resolved here from the canonical field name.
const resolveFieldType = (canonicalKey) => {
  const mappedType = getFieldType(canonicalKey);
  if (mappedType) {
    return mappedType;
  }
  return isDateField(canonicalKey) ? 'date' : 'text';
};

const resolveFieldOptions = (canonicalKey, fieldType, comboBoxOptions) => {
  if (fieldType === 'radio') {
    return getRadioOptions(canonicalKey);
  }
  if (fieldType === 'comboBox') {
    return comboBoxOptions?.[canonicalKey] || [];
  }
  return [];
};

const renderField = (field, formik, readOnly, comboBoxOptions) => {
  const fieldKey = getFieldKey(field);
  const canonicalKey = getCanonicalKey(field);
  const fieldType = resolveFieldType(canonicalKey);
  const displayValue = isDateField(canonicalKey)
    ? prepValueForDisplay(parseEIVDateStringToEfixDateString(field.value))
    : prepValueForDisplay(field.value);
  const showError = shouldShowFieldError(formik, fieldKey);

  const handleFieldChange = (fieldNameOrEvent, nextValue) => {
    if (typeof fieldNameOrEvent === 'object' && fieldNameOrEvent?.target) {
      formik.handleChange(fieldNameOrEvent);
      return;
    }
    formik.setFieldValue(fieldNameOrEvent, nextValue, true);
    formik.setFieldTouched(fieldNameOrEvent, true, false);
  };

  return (
    <div key={field.id} className="message-fields__row">
      <FormField
        name={fieldKey}
        formik={readOnly ? null : formik}
        type={fieldType}
        label={resolveFieldLabel(field, canonicalKey)}
        options={resolveFieldOptions(canonicalKey, fieldType, comboBoxOptions)}
        value={readOnly ? displayValue : undefined}
        oldValue=""
        fullWidth
        disabled={readOnly}
        readOnly={readOnly}
        isEdited={false}
        id={String(field.id)}
        onChange={readOnly ? undefined : handleFieldChange}
        onBlur={readOnly ? undefined : formik.handleBlur}
        error={readOnly ? undefined : showError}
        helperText={readOnly || !showError ? '' : formik.errors[fieldKey] || ''}
        radioClassName="message-fields__radio-field"
        radioGroupClassName="message-fields__radio-group"
        radioLabelClassName="message-fields__label"
      />
    </div>
  );
};

const resolveCountryDisplay = (countryCode) => {
  if (countryCode === null || countryCode === undefined || countryCode === '') {
    return prepValueForDisplay('');
  }

  const code = mapCountryNameToCode(countryCode);
  return (
    COUNTRY_OPTIONS.find((option) => option.value === code)?.title ?? prepValueForDisplay(code)
  );
};

const shouldShowEntityFieldError = (entityFormik, canonicalKey) =>
  Boolean(
    entityFormik?.errors?.[canonicalKey] &&
      (entityFormik?.touched?.[canonicalKey] || entityFormik?.submitCount > 0)
  );

const renderCountryField = (entityFormik, readOnly, countryCode) => {
  if (readOnly) {
    const displayValue = resolveCountryDisplay(
      countryCode ? mapCountryNameToCode(countryCode) : ''
    );

    return (
      <InputRow>
        <FormField
          name={COMPANY_FIELDS.COUNTRY}
          formik={null}
          type="text"
          label={resolveFieldLabel({ field: COMPANY_FIELDS.COUNTRY }, COMPANY_FIELDS.COUNTRY)}
          value={displayValue}
          oldValue=""
          fullWidth
          disabled
          readOnly
          isEdited={false}
        />
      </InputRow>
    );
  }

  const showError = shouldShowEntityFieldError(entityFormik, COMPANY_FIELDS.COUNTRY);

  const handleCountryChange = (name, value) => {
    entityFormik.setFieldValue(name, value);
    FIELDS_TO_CLEAR_AFTER_COUNTRY.forEach((fieldName) => {
      entityFormik.setFieldValue(fieldName, '', false);
    });
  };

  return (
    <InputRow>
      <FormField
        name={COMPANY_FIELDS.COUNTRY}
        formik={entityFormik}
        type="comboBox"
        label={resolveFieldLabel({ field: COMPANY_FIELDS.COUNTRY }, COMPANY_FIELDS.COUNTRY)}
        options={COUNTRY_OPTIONS}
        oldValue={entityFormik.initialValues[COMPANY_FIELDS.COUNTRY] ?? ''}
        fullWidth
        onChange={handleCountryChange}
        onBlur={entityFormik.handleBlur}
        error={showError}
        helperText={showError ? entityFormik.errors[COMPANY_FIELDS.COUNTRY] || '' : ''}
      />
    </InputRow>
  );
};

const renderManualAddressFields = (entityFormik, readOnly, values) => {
  const renderManualField = (canonicalKey) => {
    const displayValue = prepValueForDisplay(values[canonicalKey]);
    const showError = entityFormik ? shouldShowEntityFieldError(entityFormik, canonicalKey) : false;

    return (
      <FormField
        key={canonicalKey}
        name={canonicalKey}
        formik={readOnly ? null : entityFormik}
        type="text"
        label={resolveFieldLabel({ field: canonicalKey }, canonicalKey)}
        value={readOnly ? displayValue : undefined}
        oldValue=""
        fullWidth
        disabled={readOnly}
        readOnly={readOnly}
        isEdited={false}
        onChange={readOnly ? undefined : entityFormik?.handleChange}
        onBlur={readOnly ? undefined : entityFormik?.handleBlur}
        error={readOnly ? undefined : showError}
        helperText={readOnly || !showError ? '' : entityFormik.errors[canonicalKey] || ''}
      />
    );
  };

  return (
    <div className="address-input">
      <InputRow>
        {renderManualField(COMPANY_FIELDS.POSTAL_CODE)}
        {renderManualField(COMPANY_FIELDS.CITY)}
        {renderManualField(COMPANY_FIELDS.DISTRICT)}
      </InputRow>
      <InputRow>
        {renderManualField(COMPANY_FIELDS.STREET)}
        {renderManualField(COMPANY_FIELDS.HOUSE_NUMBER)}
        {renderManualField(COMPANY_FIELDS.HOUSE_NUMBER_ADDITION)}
      </InputRow>
    </div>
  );
};

const renderAddressInput = (addressFields, entityId, formik, readOnly) => {
  if (!addressFields.length) {
    return null;
  }

  const addressValues = buildAddressValuesFromFields(addressFields);
  const countryValue = addressValues[COMPANY_FIELDS.COUNTRY] ?? '';
  const isGermany = countryValue === GERMANY_COUNTRY_CODE;

  if (readOnly) {
    return (
      <>
        {renderCountryField(null, true, countryValue)}
        {isGermany ? (
          <div className="message-fields__row">
            <AddressInput readOnly values={addressValues} />
          </div>
        ) : (
          <div className="message-fields__row">
            {renderManualAddressFields(null, true, addressValues)}
          </div>
        )}
      </>
    );
  }

  const entityFormik = createEntityScopedFormik(formik, entityId);
  if (!entityFormik) {
    return null;
  }

  const selectedCountry = entityFormik.values[COMPANY_FIELDS.COUNTRY] ?? '';
  const showGermanAddress = selectedCountry === GERMANY_COUNTRY_CODE;

  return (
    <>
      {renderCountryField(entityFormik, false)}
      <div className="message-fields__row">
        {showGermanAddress ? (
          <AddressInput formik={entityFormik} showErrorsWhenTouched />
        ) : (
          renderManualAddressFields(entityFormik, false, entityFormik.values)
        )}
      </div>
    </>
  );
};

export const MessageFields = ({
  fields,
  formik,
  entityNameMap,
  comboBoxOptions,
  readOnly = false,
}) => {
  if (!fields?.length) {
    return null;
  }
  if (!readOnly && !formik) {
    return null;
  }

  const groups = fields.reduce((acc, field) => {
    const key = `${field.entity}_${field.entityId}`;
    if (!acc[key]) acc[key] = [];
    acc[key].push(field);
    return acc;
  }, {});

  const groupEntries = Object.entries(groups);

  return (
    <div className="message-fields">
      {groupEntries.map(([groupKey, groupFields], index) => {
        const entityName = entityNameMap?.[groupKey] || groupFields[0]?.entityLabel || null;
        const addressFields = groupFields.filter(isAddressMessageField);
        const otherFields = groupFields.filter((field) => !isAddressMessageField(field));
        const entityId = groupFields[0]?.entityId;

        return (
          <React.Fragment key={groupKey}>
            <div className="message-fields__group">
              {entityName && <div className="message-fields__entity-name">{entityName}</div>}
              {otherFields.map((field) => renderField(field, formik, readOnly, comboBoxOptions))}
              {renderAddressInput(addressFields, entityId, formik, readOnly)}
            </div>
            {index === 0 && groupEntries.length > 1 && <DividingLine />}
          </React.Fragment>
        );
      })}
    </div>
  );
};

MessageFields.propTypes = {
  fields: PropTypes.arrayOf(MessageFieldType),
  formik: PropTypes.shape({
    errors: PropTypes.object,
    handleChange: PropTypes.func,
    values: PropTypes.object,
  }),
  readOnly: PropTypes.bool,
  entityNameMap: PropTypes.object,
  comboBoxOptions: PropTypes.object,
};

MessageFields.defaultProps = {
  fields: [],
  formik: null,
  readOnly: false,
  entityNameMap: {},
  comboBoxOptions: {},
};
